var _a;
import { css, ReactiveElement } from '@folkjs/dom/ReactiveElement';
import * as R from '@folkjs/geometry/Rect2D';
import * as S from '@folkjs/geometry/Shape2D';
import * as V from '@folkjs/geometry/Vector2';
/**
 * Visual debugging system that renders canvas overlays in DOM containers.
 *
 * Creates full-size canvas overlays that can be placed anywhere in the DOM.
 * Supports multiple instances with isolated drawing layers.
 *
 * Usage:
 * ```html
 * <folk-gizmos layer="debug"></folk-gizmos>
 * ```
 *
 * Drawing methods:
 * ```ts
 * Gizmos.point({x, y});
 * Gizmos.line(start, end, { color: 'red' });
 * Gizmos.rect(domRect, { fill: 'blue' });
 * Gizmos.vector(origin, vector, { color: 'blue', width: 2, size: 10 });
 * ```
 */
export class Gizmos extends ReactiveElement {
    static { this.tagName = 'folk-gizmos'; }
    static #layers = new Map();
    static #defaultLayer = 'default';
    static #hasLoggedDrawWarning = false;
    static #hasLoggedInitMessage = false;
    static { this.styles = css `
    :host {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: calc(Infinity);
    }

    .gizmos-canvas {
      position: absolute;
      width: 100%;
      height: 100%;
    }
  `; }
    #layer;
    #hidden;
    constructor() {
        super();
        this.#layer = this.getAttribute('layer') ?? _a.#defaultLayer;
        this.#hidden = this.hasAttribute('hidden');
    }
    createRenderRoot() {
        const root = super.createRenderRoot();
        root.setHTMLUnsafe(`<canvas class="gizmos-canvas"></canvas> `);
        const canvas = root.querySelector('.gizmos-canvas');
        const ctx = canvas?.getContext('2d') ?? null;
        if (canvas && ctx) {
            _a.#layers.set(this.#layer, { canvas, ctx, hidden: this.#hidden });
        }
        this.#handleResize();
        window.addEventListener('resize', () => this.#handleResize());
        return root;
    }
    /** Draws a point */
    static point(point, { color = 'red', size = 5, layer = _a.#defaultLayer } = {}) {
        const ctx = _a.#getContext(layer);
        if (!ctx)
            return;
        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(point.x, point.y, size, 0, Math.PI * 2);
        ctx.fill();
    }
    /** Draws a line between two points */
    static line(start, end, { color = 'blue', width = 2, dashed = false, layer = _a.#defaultLayer } = {}) {
        const ctx = _a.#getContext(layer);
        if (!ctx)
            return;
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.setLineDash(dashed ? [5, 5] : []);
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
    }
    /** Draws a rectangle, can be a regular Rect2D or Shape2D */
    static rect(rect, { color = 'blue', width = 2, fill, layer = _a.#defaultLayer } = {}) {
        const ctx = _a.#getContext(layer);
        if (!ctx)
            return;
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        if (S.isShape2D(rect)) {
            // For transformed rectangles, draw using the vertices
            const vertices = S.absoluteVertices(rect);
            ctx.moveTo(vertices[0].x, vertices[0].y);
            for (let i = 1; i < vertices.length; i++) {
                ctx.lineTo(vertices[i].x, vertices[i].y);
            }
            ctx.closePath();
        }
        else {
            // For regular DOMRects, draw a simple rectangle
            ctx.rect(rect.x, rect.y, rect.width, rect.height);
        }
        if (fill) {
            ctx.fillStyle = fill;
            ctx.fill();
        }
        ctx.stroke();
    }
    /**
     * Draws a vector with an arrow head starting from `origin`.
     * @param origin The starting point
     * @param vector The vector endpoint in local coordinates relative to origin
     */
    static vector(origin, localEndpoint, { color = 'blue', width = 2, size = 10, layer = _a.#defaultLayer } = {}) {
        const ctx = _a.#getContext(layer);
        if (!ctx)
            return;
        // Convert local endpoint to global coordinates
        const globalEndpoint = V.add(origin, localEndpoint);
        // Calculate angle and normalized direction
        const angle = V.angle(localEndpoint);
        const length = V.magnitude(localEndpoint);
        // Calculate where the line should end (where arrow head begins)
        const lineEnd = V.add(origin, V.scale(V.normalized(localEndpoint), length - size));
        // Draw the main line
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.moveTo(origin.x, origin.y);
        ctx.lineTo(lineEnd.x, lineEnd.y);
        ctx.stroke();
        // Draw arrow head as a connected triangle
        const arrowAngle = Math.PI / 6; // 30 degrees
        const leftPoint = V.add(globalEndpoint, V.rotate({ x: -size, y: 0 }, angle - arrowAngle));
        const rightPoint = V.add(globalEndpoint, V.rotate({ x: -size, y: 0 }, angle + arrowAngle));
        ctx.beginPath();
        ctx.moveTo(globalEndpoint.x, globalEndpoint.y);
        ctx.lineTo(leftPoint.x, leftPoint.y);
        ctx.lineTo(rightPoint.x, rightPoint.y);
        ctx.lineTo(globalEndpoint.x, globalEndpoint.y);
        ctx.fillStyle = color;
        ctx.fill();
    }
    /** Draws a text label at a point */
    static text(text, point, { color = 'black', fontSize = 12, layer = _a.#defaultLayer } = {}) {
        const ctx = _a.#getContext(layer);
        if (!ctx)
            return;
        ctx.font = `${fontSize}px Arial`;
        ctx.fillStyle = color;
        ctx.fillText(text, point.x, point.y);
    }
    /** Clears drawings from a specific layer or all layers if no layer specified */
    static clear(layer) {
        if (layer) {
            const layerData = _a.#layers.get(layer);
            if (!layerData)
                return;
            const { ctx, canvas } = layerData;
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
        else {
            // Clear all layers
            _a.#layers.forEach(({ ctx, canvas }) => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
            });
        }
    }
    #handleResize() {
        const layerData = _a.#layers.get(this.#layer);
        if (!layerData)
            return;
        const { canvas, ctx } = layerData;
        const rect = this.getBoundingClientRect();
        const dpr = window.devicePixelRatio;
        canvas.width = rect.width * dpr;
        canvas.height = rect.height * dpr;
        ctx.scale(dpr, dpr);
    }
    static #log() {
        return {
            message: '',
            styles: [],
            text(str) {
                this.message += str;
                return this;
            },
            color(str, color) {
                this.message += '%c' + str + '%c';
                this.styles.push(`font-weight: bold; color: ${color}`, '');
                return this;
            },
            print() {
                console.info(this.message, ...this.styles);
            },
        };
    }
    static #getContext(layer = _a.#defaultLayer) {
        if (!_a.#hasLoggedInitMessage) {
            const gizmos = document.querySelectorAll(_a.tagName);
            const layers = Array.from(_a.#layers.entries());
            const log = _a.#log()
                .color('Gizmos', '#4CAF50')
                .text('\n• Gizmo elements: ' + gizmos.length)
                .text('\n• Layers: [');
            layers.forEach(([key, value], i) => {
                if (i > 0)
                    log.text(', ');
                log.text(key);
                if (value.hidden) {
                    log.color(' (hidden)', '#FFA500');
                }
            });
            log.text(']').print();
            _a.#hasLoggedInitMessage = true;
        }
        const layerData = _a.#layers.get(layer);
        if (!layerData && !_a.#hasLoggedDrawWarning) {
            console.warn(`Gizmos cannot draw: layer "${layer}" not found`);
            _a.#hasLoggedDrawWarning = true;
            return null;
        }
        return layerData?.hidden ? null : layerData?.ctx;
    }
}
_a = Gizmos;
Gizmos.define();
//# sourceMappingURL=folk-gizmos.js.map